// logic
// components
import '../ui/emergencyPopup.css';
import { useHistory } from 'react-router-dom';
import quoteFormSchema from '../quoteForm/config/quoteFormSchema';
import useTicketDetail from '../ticketDetail/useTicketDetail';
import quoteService from 'modules/emergencies/quoteService';
import useSWR, { mutate } from 'swr';
import useAcceptQuoteModal from '../modals/useAcceptQuoteModal';
import useRejectQuoteModal from '../modals/useRejectQuoteModal';
import useConfirmQuoteEditionAfterAcceptance from '../modals/useConfirmQuoteEditionAfterAcceptance';

const quoteDetailFetcher = async (...args) => {
  // url has this shape
  // ticket/14/quotes/12/
  const clean = args[0].replace('ticket/', '').replace('quotes/', '');
  const cleanParts = clean.split('/');
  const ticketId = cleanParts[1];
  const quoteId = cleanParts[2];
  return quoteService.callBackend.getQuoteDetail({ ticketId, quoteId });
};

const useQuoteDetail = ({ ticketId, quoteId }) => {
  const history = useHistory();

  const { isLoadingTicketDetail, ticketDetail, ticketDetailError } = useTicketDetail({
    id: ticketId,
  });

  const { data: quoteDetail, error: quoteDetailError } = useSWR(
    `tickets/${ticketId}/quotes/${quoteId}/`,

    quoteDetailFetcher,
    { errorRetryCount: 0, revalidateOnFocus: false, revalidateOnReconnect: false }
  );
  const isLoadingQuoteDetail = typeof ticketDetail === 'undefined' && !ticketDetailError;

  const validationSchema = quoteFormSchema;

  let initialValues = quoteService.defaultValues.getDefaultInitialFormValues();
  if (!!ticketDetail && !!quoteDetail) {
    initialValues =
      quoteService.dataTransformation.getInitialQuoteDetailFormValuesFromTicketDetailAndQuoteDetail(
        {
          ticketDto: ticketDetail,
          quoteDto: quoteDetail,
        }
      );
  }
  const isEdit = quoteService.bussiness.canQuoteBeEdited(ticketDetail);
  const isStateEdit = quoteService.bussiness.canQuoteStateBeUpdated(ticketDetail);
  const isReadOnly = !isEdit;

  const { openAcceptQuoteModal } = useAcceptQuoteModal(ticketDetail, quoteDetail);
  const { openRejectQuoteModal } = useRejectQuoteModal(ticketDetail, quoteDetail);

  const canQuoteBeEditedAfterAcceptance =
    quoteService.bussiness.canQuoteBeEditedAfterAcceptance(ticketDetail);

  const { openConfirmQuoteEditionAfterAcceptanceModal } = useConfirmQuoteEditionAfterAcceptance(
    ticketDetail,
    quoteDetail
  );

  const onSubmit = async ({ values }) => {
    let updateQuoteDetailDto = quoteService.dataTransformation.getUpdateQuoteDetailDto({
      originalQuote: quoteDetail,
      newValues: values,
    });
    const changes = quoteService.bussiness.getQuoteChangesAfterEditing(
      { ...initialValues },
      { ...values }
    );
    const hasChanges = Object.keys(changes).length > 0;
    const changesThatDontTriggerModalAfterAcceptance =
      quoteService.bussiness.getQuoteChangesThatDontTriggerModalAfterAcceptance(
        { ...initialValues },
        { ...values }
      );
    const hasChangesThatDontTriggerModalAfterAcceptance =
      Object.keys(changesThatDontTriggerModalAfterAcceptance).length > 0;
    if (!hasChanges && !hasChangesThatDontTriggerModalAfterAcceptance) {
      return 'canceled-saving-because-of-no-changes';
    }
    if (canQuoteBeEditedAfterAcceptance && hasChanges) {
      const editionAfterAcceptanceChangeReasons = await openConfirmQuoteEditionAfterAcceptanceModal(
        changes
      );
      if (editionAfterAcceptanceChangeReasons === undefined) {
        return 'canceled-saving-from-confirm-quote-edition-after-acceptance';
      } else {
        updateQuoteDetailDto = {
          ...updateQuoteDetailDto,
          ...editionAfterAcceptanceChangeReasons,
        };
      }
    }
    await quoteService.callBackend.updateQuoteDetail({
      ticketId,
      quoteId,
      updateQuoteDetailDto,
    });
    // we are mutating the tickets cache so it will reload automatically
    mutate((key) => {
      return typeof key === 'string' && key.startsWith('tickets');
    });
    quoteService.utils.navigateToList({ history });
  };

  const onCancel = () => {
    history.push('/emergencies');
  };

  const onAcceptQuote = () => {
    openAcceptQuoteModal();
  };

  const onRejectQuote = () => {
    openRejectQuoteModal();
  };

  return {
    onSubmit,
    onCancel,
    onAcceptQuote,
    onRejectQuote,
    validationSchema,
    initialValues,
    isReadOnly,
    isEdit,
    isStateEdit,
    isLoadingTicketDetail,
    ticketDetail,
    ticketDetailError,
    quoteDetail,
    isLoadingQuoteDetail,
    quoteDetailError,
  };
};

export default useQuoteDetail;
